/* ModalErrorRenderer.js */

"use strict";
const _ = require("lodash");
_.mixin(require("underscore.string").exports());
const ButtonControl = require("@gluenetworks/button_control");

class ModalErrorRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("modal-error-gl");
        this._rootElem.component = this._model;

        this._confirmButton = new ButtonControl({
            darkTheme: true,
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("ErrorUpdated", (this._onErrorUpdated = this._onErrorUpdated.bind(this)));
        this._model.on(
            "ErrorCodeUpdated",
            (this._onErrorCodeUpdated = this._onErrorCodeUpdated.bind(this))
        );
        this._model.on(
            "ErrorMsgUpdated",
            (this._onErrorMsgUpdated = this._onErrorMsgUpdated.bind(this))
        );
        this._model.on("TitleChanged", (this._onTitleChanged = this._onTitleChanged.bind(this)));
        this._model.on(
            "DarkThemeChanged",
            (this._onDarkThemeChanged = this._onDarkThemeChanged.bind(this))
        );
        this._confirmButton.on(
            "UserClick",
            (this._onConfirmClicked = this._onConfirmClicked.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("ErrorUpdated", this._onErrorUpdated);
        this._model.removeListener("ErrorUpdated", this._onErrorCodeUpdated);
        this._model.removeListener("ErrorUpdated", this._onErrorMsgUpdated);
        this._model.removeListener("TitleChanged", this._onTitleChanged);
        this._model.removeListener("DarkThemeChanged", this._onDarkThemeChanged);
        this._confirmButton.removeListener("UserClick", this._onConfirmClicked);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        return this._confirmButton.shutdown().then(() => {
            this._confirmButton = undefined;
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
        });
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._rootElem.innerHTML = `
			<div class="error_contents">
	            <h2 class="error_title"></h2>
	            <div class="error_msg"></div>
	            <span class="error_code italic"></span>
            </div>
            <div class="actions_container"></div>
        `;

        this._actionsContainer = this._rootElem.querySelector(".actions_container");

        this._renderErrorTitle()
            ._renderErrorMsg()
            ._renderErrorButton()
            ._renderErrorCode()
            ._renderDarkTheme();

        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderErrorCode() {
        const errorCode = this._model.getErrorCode();
        const errorCodeElem = this._rootElem.querySelector(".error_code");

        if (errorCode === "") {
            return this;
        }
        errorCodeElem.innerText = `(GEC: ${errorCode})`;
        return this;
    }

    _renderErrorMsg() {
        let errorMsgElem = this._rootElem.querySelector(".error_msg");
        if (this._model.isHTMLErrorMsg()) {
            errorMsgElem.innerHTML = `${this._model.getErrorMsg()}`;
        } else {
            errorMsgElem.innerText = `${this._model.getErrorMsg()}`;
        }
        return this;
    }

    _renderErrorTitle() {
        let errorTitleElem = this._rootElem.querySelector(".error_title");
        errorTitleElem.innerText = `${_.titleize(_.humanize(this._model.getErrorTitle()))}`;
        return this;
    }

    _renderErrorButton() {
        this._confirmButton.label = this.model.confirmationDeets.text;
        if (!this._actionsContainer.hasChildNodes()) {
            this._actionsContainer.appendChild(this._confirmButton.renderer.rootElement);
        }
        return this;
    }

    _renderDarkTheme() {
        if (this._model.isDarkTheme()) {
            this._rootElem.classList.add("dark_theme");
        } else {
            this._rootElem.classList.remove("dark_theme");
        }
        return this;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onErrorUpdated(/*newError, oldError*/) {
        this.render();
    }

    _onErrorCodeUpdated(/*newErrorCode, oldErrorCode*/) {
        this._renderErrorCode();
    }

    _onErrorMsgUpdated(/*newErrorMsg, oldErrorMsg*/) {
        this._renderErrorMsg();
    }

    _onTitleChanged(/*newErrorTitle, oldErrorTitle*/) {
        this._renderErrorTitle();
    }

    _onDarkThemeChanged(/*newDarkThemeState, oldDarkThemeState*/) {
        this._renderDarkTheme();
    }

    _onConfirmClicked() {
        this._model.confirm();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ModalErrorRenderer;
